import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Pill,
  Calendar,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  ShieldCheck,
  X,
  Clock,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { medicalRecordsApi } from '@/api';
import type { Prescription } from '@/api/http-client';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { MeMedStatusBadge } from '@/components/memed/MeMedStatusBadge';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';

interface PrescriptionUI extends Prescription {
  doctorName: string;
  specialty: string;
  crm?: string;
  diagnosis: string;
  status?: string;
  memedId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
  memedPdfUrl?: string | null;
}

interface PrescriptionsResponse {
  prescriptions: PrescriptionUI[];
  total: number;
  page: number;
  pageSize: number;
}

export function PrescriptionsPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data, isLoading, isError, isFetching, refetch } = useQuery<PrescriptionsResponse, Error>({
    queryKey: ['patient', 'prescriptions', page],
    queryFn: async () => {
      const response = await medicalRecordsApi.getPatientPrescriptions(page, 10);
      const list = Array.isArray(response?.data) ? response.data : [];
      return {
        prescriptions: list.map((presc: any) => ({
          ...presc,
          doctorName: presc.doctorName || presc.doctorId || 'Médico Responsável',
          specialty: presc.doctorSpecialty || presc.specialty || 'Clínica Geral',
          crm: presc.doctorCrm || '',
          diagnosis: presc.instructions || presc.diagnosis || '',
          memedId: presc.memedPrescriptionId || presc.memedId,
          memedStatus: presc.memedStatus || presc.status || (presc.memedPdfUrl ? 'SIGNED' : undefined),
          memedPdfUrl:
            presc.memedPdfUrl ||
            presc.pdfUrl ||
            presc.content?.pdfUrl ||
            presc.content?.digitalPrescription?.link ||
            null,
        })),
        total: response?.meta?.total ?? list.length,
        page: response?.meta?.page ?? page,
        pageSize: response?.meta?.limit ?? 10,
      };
    },
  });

  const handleCopyPrescription = async (presc: PrescriptionUI) => {
    const medsText = (presc.medications || [])
      .map(
        (m, idx) =>
          `${idx + 1}. ${m.name} ${m.concentration || ''} ${m.presentation || ''}
   Dose: ${m.dose} | Frequência: ${m.frequency} | Duração: ${m.duration}${
            m.observations ? `\n   Obs: ${m.observations}` : ''
          }`
      )
      .join('\n\n');

    const validationUrl = presc.memedPdfUrl || presc.pdfUrl || 'Aguardando validação na MeMed';

    const textToCopy = `*Receita Médica - Medikei*
Médico: ${presc.doctorName} (${presc.specialty}${presc.crm ? ` • ${presc.crm}` : ''})
Data: ${formatDate(presc.createdAt)}
${presc.diagnosis ? `Diagnóstico / Indicação: ${presc.diagnosis}\n` : ''}*Medicamentos Prescritos:*
${medsText || 'Nenhum medicamento especificado'}

Validação Digital ICP-Brasil (MeMed):
${validationUrl}

ID da Receita: ${presc.id}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedId(presc.id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      setCopiedId(presc.id);
      setTimeout(() => setCopiedId(null), 2500);
    }
  };

  const handleViewPdf = async (prescription: PrescriptionUI) => {
    const targetUrl = prescription.memedPdfUrl || prescription.pdfUrl;
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (prescription.memedStatus === 'PENDING') {
      toast({
        title: 'Assinatura Pendente',
        description: 'Esta receita ainda está aguardando a assinatura digital oficial na MeMed',
      });
      return;
    }

    try {
      setDownloadingId(prescription.id);
      if (prescription.memedId || prescription.memedPrescriptionId || prescription.id) {
        const linkRes = await medicalRecordsApi
          .getDigitalPrescriptionLink(prescription.id)
          .catch(() => null);
        if (linkRes?.link) {
          window.open(linkRes.link, '_blank', 'noopener,noreferrer');
          return;
        }
      }

      const res = await medicalRecordsApi.downloadPrescriptionPdf(prescription.id);
      if ((res as any)?.url) {
        window.open((res as any).url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (res?.pdf) {
        const blob = await fetch(`data:${res.mimeType || 'application/pdf'};base64,${res.pdf}`).then((r) => r.blob());
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      toast({
        title: 'Assinatura Pendente',
        description: 'Esta receita ainda está em processamento pela MeMed. Tente novamente em instantes.',
      });
    } catch (err: any) {
      toast({
        title: 'Visualização Indisponível',
        description: err?.message || 'A receita médica ainda aguarda processamento do PDF na MeMed.',
        variant: 'error',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadPdf = async (prescription: PrescriptionUI) => {
    const targetUrl = prescription.memedPdfUrl || prescription.pdfUrl;
    if (prescription.memedStatus === 'PENDING' && !targetUrl) {
      toast({
        title: 'Assinatura Pendente',
        description: 'Esta receita ainda está aguardando a assinatura digital oficial na MeMed',
      });
      return;
    }

    try {
      setDownloadingId(prescription.id);
      const targetUrl = prescription.memedPdfUrl || prescription.pdfUrl;
      if (targetUrl) {
        const link = document.createElement('a');
        link.href = targetUrl;
        link.target = '_blank';
        link.rel = 'noopener,noreferrer';
        link.download = `receita_memed_${prescription.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      const res = await medicalRecordsApi.downloadPrescriptionPdf(prescription.id);
      if ((res as any)?.url) {
        window.open((res as any).url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (res?.pdf) {
        const link = document.createElement('a');
        link.href = `data:${res.mimeType || 'application/pdf'};base64,${res.pdf}`;
        link.download = `receita_memed_${prescription.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      if (prescription.memedId || prescription.memedPrescriptionId || prescription.id) {
        const linkRes = await medicalRecordsApi
          .getDigitalPrescriptionLink(prescription.id)
          .catch(() => null);
        if (linkRes?.link) {
          window.open(linkRes.link, '_blank', 'noopener,noreferrer');
          return;
        }
      }

      toast({
        title: 'Processando Assinatura',
        description: 'A receita foi emitida e está aguardando geração do arquivo PDF pela MeMed.',
      });
    } catch (err: any) {
      toast({
        title: 'Download Indisponível',
        description: err?.message || 'A receita médica ainda aguarda processamento do PDF na MeMed.',
        variant: 'error',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredPrescriptions = (data?.prescriptions || []).filter((p) => {
    const term = search.toLowerCase().trim();
    if (!term) return true;
    return (
      p.doctorName.toLowerCase().includes(term) ||
      p.specialty.toLowerCase().includes(term) ||
      (p.crm && p.crm.toLowerCase().includes(term)) ||
      p.diagnosis.toLowerCase().includes(term) ||
      p.medications.some((m) => m.name.toLowerCase().includes(term))
    );
  });

  const formatDate = (dateStr?: string | Date | null) => {
    if (!dateStr) return '--';
    try {
      const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0] : '';
      if (datePart && datePart.includes('-')) {
        const [year, month, day] = datePart.split('-');
        if (day && month && year) return `${day}/${month}/${year}`;
      }
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return '--';
    }
  };

  const totalPages = data && data.pageSize ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header com Estatística e Ação de Atualizar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              Minhas Receitas
            </h1>
            {data && data.total > 0 && (
              <Badge
                variant="outline"
                className="font-bold text-xs bg-primary/10 text-primary border-primary/20"
              >
                {data.total}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Acesse, visualize e faça o download das suas prescrições e receitas médicas digitais
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="gap-2 rounded-xl font-semibold shadow-xs hover:bg-muted w-full sm:w-auto h-11"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-primary' : ''}`} />
          <span>Atualizar</span>
        </Button>
      </div>

      {/* Barra de Busca com Limpeza Rápida */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Buscar por médico, diagnóstico ou medicamento..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
            aria-label="Limpar busca"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Estados de Carregamento, Erro ou Vazio */}
      {isLoading ? (
        <LoadingOverlay message="Carregando receitas..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar receitas" onRetry={() => refetch()} />
      ) : filteredPrescriptions.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 sm:p-12 text-center bg-card">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center mx-auto mb-4 shadow-xs">
            <Pill className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            {search ? 'Nenhuma receita encontrada' : 'Nenhuma receita encontrada'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {search
              ? `Não encontramos nenhuma prescrição correspondente a "${search}". Tente buscar por outro termo.`
              : 'Suas receitas médicas emitidas durante consultas presenciais ou telemedicina aparecerão aqui para download e visualização.'}
          </p>
          {search ? (
            <Button
              variant="outline"
              onClick={() => setSearch('')}
              className="rounded-xl font-semibold"
            >
              Limpar busca
            </Button>
          ) : (
            <Button
              onClick={() => refetch()}
              className="rounded-xl font-bold gap-2 shadow-md shadow-primary/20"
            >
              <RefreshCw className="w-4 h-4" />
              Verificar Novas Receitas
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPrescriptions.map((prescription) => {
            const isMemedConfirmed =
              prescription.memedStatus === 'SIGNED' ||
              prescription.status === 'SIGNED' ||
              Boolean(prescription.memedPdfUrl) ||
              Boolean(prescription.memedPrescriptionId && prescription.memedStatus !== 'PENDING');
            const hasOfficialPdf = Boolean(isMemedConfirmed && prescription.memedStatus !== 'PENDING');
            const isCopied = copiedId === prescription.id;

            return (
              <Card
                key={prescription.id}
                className="rounded-2xl border border-border bg-card shadow-xs hover:shadow-md hover:border-primary/40 transition-all overflow-hidden"
              >
                <CardContent className="p-5 sm:p-6">
                  {/* Cabeçalho do Card */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5 pb-4 border-b border-border/60">
                    <div className="flex items-start gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 border border-primary/25 flex items-center justify-center text-primary shrink-0 shadow-xs">
                        <Pill className="h-6 w-6" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-bold text-lg text-foreground tracking-tight truncate">
                            {prescription.doctorName}
                          </h3>
                          {prescription.crm && (
                            <Badge
                              variant="outline"
                              className="text-[11px] font-semibold text-muted-foreground border-border/80"
                            >
                              {prescription.crm}
                            </Badge>
                          )}
                        </div>

                        <p className="text-sm font-semibold text-primary mt-0.5">
                          {prescription.specialty}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-lg border border-border/40">
                            <Calendar className="h-3.5 w-3.5 text-primary" />
                            {formatDate(prescription.createdAt)}
                          </span>

                          {isMemedConfirmed ? (
                            <Badge
                              variant="outline"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                            >
                              <ShieldCheck className="h-3.5 w-3.5" />
                              Assinada Digitalmente (ICP-Brasil)
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                            >
                              <Clock className="h-3.5 w-3.5" />
                              Pendente de Assinatura MeMed
                            </Badge>
                          )}

                          {prescription.memedId && (
                            <MeMedStatusBadge
                              status={prescription.memedStatus || 'PENDING'}
                              memedId={prescription.memedId}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Ações da Receita */}
                    <div className="flex items-center gap-2 self-end sm:self-start shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyPrescription(prescription)}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-9 hover:bg-muted"
                        title="Copiar texto da receita"
                      >
                        {isCopied ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-emerald-600 font-bold">Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>Copiar</span>
                          </>
                        )}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewPdf(prescription)}
                        className="rounded-xl text-xs font-semibold gap-1.5 h-9 hover:bg-muted"
                        title="Visualizar receita em nova aba"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        <span>Visualizar</span>
                      </Button>

                      <Button
                        size="sm"
                        onClick={() => handleDownloadPdf(prescription)}
                        disabled={!hasOfficialPdf || downloadingId === prescription.id}
                        className="rounded-xl text-xs font-bold gap-1.5 h-9 shadow-md shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          hasOfficialPdf
                            ? 'Baixar PDF oficial da MeMed'
                            : 'Esta receita ainda está aguardando a assinatura digital oficial na MeMed'
                        }
                      >
                        {downloadingId === prescription.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        <span>Baixar PDF</span>
                      </Button>
                    </div>
                  </div>

                  {/* Painel da Receita Digital & PIN da Farmácia para o Paciente */}
                  {(() => {
                    const unlockCode =
                      (prescription as any).digitalPrescription?.unlockCode ||
                      (prescription as any).content?.digitalPrescription?.unlockCode ||
                      (prescription as any).unlockCode;
                    const digitalLink =
                      (prescription as any).digitalPrescription?.link ||
                      (prescription as any).content?.digitalPrescription?.link ||
                      (prescription as any).digitalPrescriptionLink;

                    if (!unlockCode && !digitalLink && !isMemedConfirmed) {
                      return null;
                    }

                    return (
                      <div className="space-y-3 mb-4">
                        <div className="bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/60 rounded-xl p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
                              <ShieldCheck className="h-5 w-5" />
                            </div>
                            <div>
                              <span className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider block">
                                Receita Digital Oficial ICP-Brasil
                              </span>
                              {unlockCode && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-muted-foreground">Código de Desbloqueio (Farmácia):</span>
                                  <span className="font-mono font-black text-base tracking-wider text-emerald-900 dark:text-emerald-100 select-all">
                                    {unlockCode}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {digitalLink && (
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(digitalLink, '_blank')}
                                className="h-8 text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-100/50"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                <span>Link da Farmácia</span>
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Card Informativo de Cotação & Compra Online */}
                        {digitalLink && (
                          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                                <Sparkles className="h-5 w-5" />
                              </div>
                              <div>
                                <span className="font-bold text-foreground block text-xs">
                                  Cotação em Farmácias Parceiras & Entrega em Domicílio
                                </span>
                                <p className="text-muted-foreground text-[11px] mt-0.5">
                                  Compare preços médios entre redes credenciadas (Drogasil, Raia, Pague Menos) e compre online com entrega no mesmo dia.
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => window.open(digitalLink, '_blank')}
                              className="h-8 text-xs font-bold gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 shadow-xs"
                            >
                              <span>Ver Preços & Comprar Online</span>
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Diagnóstico / Instruções Gerais */}
                  {prescription.diagnosis && (
                    <div className="bg-muted/40 rounded-xl p-3.5 sm:p-4 mb-4 border border-border/60">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                        Diagnóstico / Instruções Médicas
                      </span>
                      <p className="text-sm font-medium text-foreground leading-relaxed">
                        {prescription.diagnosis}
                      </p>
                    </div>
                  )}

                  {/* Lista de Medicamentos Prescritos */}
                  <div className="space-y-2.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                      Medicamentos Prescritos ({prescription.medications?.length || 0})
                    </span>

                    <div className="grid grid-cols-1 gap-2.5">
                      {(prescription.medications || []).map((med, idx) => (
                        <div
                          key={idx}
                          className="bg-card border border-border/70 rounded-xl p-3.5 hover:border-primary/30 transition-all shadow-xs"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-extrabold flex items-center justify-center shrink-0">
                                {idx + 1}
                              </span>
                              <span className="font-bold text-sm text-foreground">
                                {med.name}
                              </span>
                              {(med.concentration || med.presentation) && (
                                <span className="text-xs text-muted-foreground">
                                  ({[med.concentration, med.presentation].filter(Boolean).join(' - ')})
                                </span>
                              )}
                            </div>

                            {med.quantity && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-muted text-foreground self-start sm:self-auto">
                                Qtd: {med.quantity}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs text-muted-foreground bg-muted/20 p-2.5 rounded-lg border border-border/40">
                            <div>
                              <strong className="text-foreground">Posologia:</strong> {med.dose || '--'}
                            </div>
                            <div>
                              <strong className="text-foreground">Frequência:</strong> {med.frequency || '--'}
                            </div>
                            <div>
                              <strong className="text-foreground">Duração:</strong> {med.duration || '--'}
                            </div>
                          </div>

                          {med.observations && (
                            <p className="text-xs text-primary font-medium mt-2 bg-primary/5 p-2 rounded-lg border border-primary/15">
                              &bull; Orientação: {med.observations}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rodapé do Card */}
                  <div className="mt-4 pt-3.5 border-t border-border/60 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-primary" />
                      <span>
                        Validade da receita:{' '}
                        <strong className="text-foreground">
                          {formatDate(prescription.validUntil || prescription.createdAt)}
                        </strong>
                      </span>
                    </span>

                    <span className="font-mono text-[11px] text-muted-foreground/80">
                      ID: {prescription.id}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Paginação (Renderizada apenas quando há mais de 1 página) */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-xl font-medium"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="px-3.5 py-1.5 bg-primary/10 text-primary font-bold text-xs rounded-xl border border-primary/20">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded-xl font-medium"
          >
            Próximo
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
